import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { EmptyState } from "@circle-react-uikit/EmptyState";
import { Button } from "@circleco/compass/components/Button";
import { useEffect } from "react";

import { t } from "@/i18n-js/instance";
import { useCommentHierarchy } from "@/react/components/CommentsCompass/data/useCommentHierarchy";
import { useCommentsConfig } from "@/react/components/CommentsCompass/Provider/ConfigContext";
import { useCommentsScroll } from "@/react/components/CommentsCompass/Provider/ScrollContext";
import { useCommentsSection } from "@/react/components/CommentsCompass/Provider/SectionContext";
import { useCommentsDeepLink } from "@/react/components/CommentsCompass/Provider/useCommentsDeepLink";
import { Thread } from "@/react/components/CommentsCompass/Thread";
import { toLocaleString } from "@/react/helpers/number";

interface HierarchyViewProps {
  commentId: number;
  commentsCount: number;
}

export const HierarchyView = ({
  commentId,
  commentsCount,
}: HierarchyViewProps) => {
  const { postId } = useCommentsConfig();
  const { showAllComments } = useCommentsDeepLink();
  const { requestScrollToComment, cancelScrollToComment } = useCommentsScroll();
  const { isTopLevelFocusPending, requestComposerFocus } = useCommentsSection();
  const { comment, isPending } = useCommentHierarchy({ postId, id: commentId });

  useEffect(() => {
    requestScrollToComment(commentId);
    return () => cancelScrollToComment(commentId);
  }, [commentId, requestScrollToComment, cancelScrollToComment]);

  // Hierarchy view has no top-level composer, so redirect top-level focus
  // requests onto this thread's reply composer. We focus the loaded comment.id
  // (the thread root), not the deep-linked commentId, which may itself be a reply.
  useEffect(() => {
    if (!comment || !isTopLevelFocusPending) return;
    requestComposerFocus({ kind: "reply", commentId: comment.id });
  }, [comment, isTopLevelFocusPending, requestComposerFocus]);

  const renderContent = () => {
    if (isPending) return <LoaderRow />;
    if (!comment) {
      return (
        <div className="flex min-h-72 items-center justify-center">
          <EmptyState
            fontSize="sm"
            header={t("comments.hierarchy.not_found_title")}
            description={t("comments.hierarchy.not_found")}
          />
        </div>
      );
    }
    return (
      <Thread comment={comment} allRepliesPreloaded defaultReplyFormOpen />
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <Button
        variant="outline"
        startIcon="arrow-left"
        onClick={showAllComments}
      >
        {t("comments.list.show_all_count_comments", {
          count: commentsCount,
          formatted_count: toLocaleString(commentsCount),
        })}
      </Button>
      {renderContent()}
    </div>
  );
};
